import { useState } from 'react';
import type { ExportFormatStructureNode } from '@capture/shared';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderChildren } from '../../form-config/export/StructureBuilder.js';
import { TextPromptModal } from '@/components/TextPromptModal';
import styles from '../../form-config/export/export-composer.module.css';

export interface FormatStructureNodeProps {
  node: ExportFormatStructureNode;
  depth: number;
  path: number[];                 // index path from the synthetic root
  selectedPath: number[] | null;  // which node is selected (for highlight)
  onSelect: (path: number[]) => void;
  onUpdate: (updated: ExportFormatStructureNode) => void;
  onDelete: () => void;
  format: 'json' | 'xml';
  sortableId?: string;
}

export function FormatStructureNode({
  node,
  depth,
  path,
  selectedPath,
  onSelect,
  onUpdate,
  onDelete,
  format,
  sortableId,
}: FormatStructureNodeProps) {
  const [groupPromptOpen, setGroupPromptOpen] = useState(false);
  const [groupPromptType, setGroupPromptType] = useState<'group' | 'collection'>('group');
  const [fieldPromptOpen, setFieldPromptOpen] = useState(false);

  // Sortable wiring — hooks must run unconditionally
  const sortable = useSortable({ id: sortableId ?? '__static__', disabled: sortableId === undefined });
  const sortableStyle = sortableId !== undefined
    ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }
    : undefined;

  const childSensors = useSensors(useSensor(PointerSensor));
  function handleChildDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = Number(active.id);
    const to = Number(over.id);
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    onUpdate({ ...node, children: reorderChildren(node.children ?? [], from, to) });
  }

  const isContainer = node.type === 'group' || node.type === 'collection';
  const canAddChildren = isContainer && depth < 5;

  // Selection: compare path arrays element-by-element
  const isSelected =
    selectedPath !== null &&
    selectedPath.length === path.length &&
    selectedPath.every((v, k) => v === path[k]);

  function handleTypeToggle() {
    if (node.type === 'group') {
      onUpdate({ ...node, type: 'collection' });
    } else if (node.type === 'collection') {
      onUpdate({ ...node, type: 'group' });
    }
  }

  function handleAddGroupNamed(name: string) {
    const newNode: ExportFormatStructureNode = { name, type: groupPromptType, children: [] };
    onUpdate({ ...node, children: [...(node.children ?? []), newNode] });
    setGroupPromptOpen(false);
    setGroupPromptType('group');
  }

  function handleAddFieldNamed(name: string) {
    const newField: ExportFormatStructureNode = {
      name,
      type: 'field',
      dataType: 'raw',
      required: false,
      allowEmpty: true,
    };
    onUpdate({ ...node, children: [...(node.children ?? []), newField] });
    setFieldPromptOpen(false);
  }

  // One sibling namespace for ALL node types — matches the save-time
  // validateFormatStructure (which rejects any duplicate sibling name
  // regardless of type). Splitting field vs group sets let a field and a group
  // share a name at add-time only to fail at Save — a confusing footgun.
  const existingSiblingNames = new Set((node.children ?? []).map((c) => c.name));

  function handleChildUpdate(index: number, updated: ExportFormatStructureNode) {
    const children = (node.children ?? []).map((c, i) => (i === index ? updated : c));
    onUpdate({ ...node, children });
  }

  function handleChildDelete(index: number) {
    const deleted = node.children?.[index];
    const deletedChildren = deleted?.children ?? [];
    const remaining = (node.children ?? []).filter((_, i) => i !== index);
    onUpdate({ ...node, children: [...remaining, ...deletedChildren] });
  }

  const nodeClass = [
    styles.structureNode,
    node.type === 'collection' ? styles.structureNodeCollection : '',
    isSelected ? styles.structureNodeSelected : '',
  ].filter(Boolean).join(' ');

  const childrenClass = node.type === 'collection'
    ? styles.structureChildrenCollection
    : styles.structureChildren;

  // Synthetic root: depth 0 with no sortableId — suppress drag handle and delete chrome
  const isSyntheticRoot = depth === 0 && sortableId === undefined;

  return (
    <div
      ref={sortableId !== undefined ? sortable.setNodeRef : undefined}
      style={{ marginLeft: depth > 0 ? 24 : 0, ...sortableStyle }}
    >
      <div className={nodeClass}>
        {/* Drag handle — hidden for the synthetic root */}
        {!isSyntheticRoot && (
          <span
            className={styles.structureDragHandle}
            aria-label="drag handle"
            {...(sortableId !== undefined ? { ...sortable.attributes, ...sortable.listeners } : {})}
          >
            ⠿
          </span>
        )}

        {/* Icon — type-aware in JSON mode, folder in XML mode */}
        {isContainer ? (
          <span style={{ fontSize: 13, fontFamily: 'monospace' }}>
            {format === 'json'
              ? (node.type === 'collection' ? '[ ]' : '{ }')
              : '📁'}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: '#6d28d9' }}>◆</span>
        )}

        {/* Name — static span, click selects this node */}
        <span
          className={isContainer ? styles.structureNodeNameContainer : styles.structureNodeName}
          onClick={() => onSelect(path)}
        >
          {node.name}
        </span>

        {/* ARRAY badge for collections */}
        {node.type === 'collection' && (
          <span className={styles.structureBadge}>
            ARRAY
          </span>
        )}

        {/* Typed slot info for field nodes */}
        {node.type === 'field' && (
          <>
            <span className={styles.structureBadge}>
              {node.dataType ?? 'raw'}
            </span>
            {node.required && (
              <span
                className={styles.structureNodeRequiredDot}
                title="Required"
              />
            )}
          </>
        )}

        {/* Type toggle for group/collection — JSON only */}
        {isContainer && format === 'json' && (
          <button
            type="button"
            className={styles.structureTypeToggleBtn}
            onClick={handleTypeToggle}
            title={node.type === 'group'
              ? 'Currently object {}. Click to make array [].'
              : 'Currently array []. Click to make object {}.'}
          >
            {node.type === 'group' ? 'Make Array' : 'Make Group'}
          </button>
        )}

        {/* Delete button — hidden for the synthetic root */}
        {!isSyntheticRoot && (
          <button
            type="button"
            className={styles.structureNodeDeleteBtn}
            onClick={onDelete}
            aria-label={`Delete ${node.name}`}
          >
            ×
          </button>
        )}
      </div>

      {/* Children — draggable/sortable */}
      {isContainer && node.children && node.children.length > 0 && (
        <DndContext
          sensors={childSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleChildDragEnd}
        >
          <SortableContext
            items={node.children.map((_, i) => String(i))}
            strategy={verticalListSortingStrategy}
          >
            <div className={childrenClass}>
              {node.children.map((child, i) => (
                <FormatStructureNode
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  sortableId={String(i)}
                  node={child}
                  depth={depth + 1}
                  path={[...path, i]}
                  selectedPath={selectedPath}
                  onSelect={onSelect}
                  onUpdate={(updated) => handleChildUpdate(i, updated)}
                  onDelete={() => handleChildDelete(i)}
                  format={format}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add affordances (containers within max depth) */}
      {canAddChildren && (
        <div className={styles.structureAddRow} style={{ marginLeft: depth > 0 ? 24 : 0 }}>
          {format === 'json' ? (
            <>
              <button
                type="button"
                className={styles.structureAddChildBtn}
                onClick={() => { setGroupPromptType('group'); setGroupPromptOpen(true); }}
                aria-label="+ Object"
              >
                + Object
              </button>
              <button
                type="button"
                className={styles.structureAddChildBtn}
                onClick={() => { setGroupPromptType('collection'); setGroupPromptOpen(true); }}
                aria-label="+ Array"
              >
                + Array
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.structureAddChildBtn}
              onClick={() => { setGroupPromptType('group'); setGroupPromptOpen(true); }}
              aria-label="+ Group"
            >
              + Group
            </button>
          )}
          <button
            type="button"
            className={styles.structureAddChildBtn}
            onClick={() => setFieldPromptOpen(true)}
            aria-label="+ Field"
          >
            + Field
          </button>
        </div>
      )}

      {/* Group/Collection prompt */}
      <TextPromptModal
        open={groupPromptOpen}
        title={groupPromptType === 'collection' ? 'Add array' : 'Add group'}
        description={
          groupPromptType === 'collection'
            ? 'Arrays let you group repeated items under a parent element in the exported document.'
            : 'Groups let you nest fields under a parent element in the exported document.'
        }
        placeholder="e.g. Address"
        submitLabel={groupPromptType === 'collection' ? 'Add array' : 'Add group'}
        validate={(name) =>
          existingSiblingNames.has(name)
            ? `A slot named "${name}" already exists at this level`
            : null
        }
        onConfirm={handleAddGroupNamed}
        onCancel={() => setGroupPromptOpen(false)}
      />

      {/* Field slot prompt */}
      <TextPromptModal
        open={fieldPromptOpen}
        title="Add field"
        description="Adds an empty typed slot. Configure the slot's type and constraints in the panel."
        placeholder="e.g. Amount"
        submitLabel="Add field"
        validate={(name) =>
          existingSiblingNames.has(name)
            ? `A slot named "${name}" already exists at this level`
            : null
        }
        onConfirm={handleAddFieldNamed}
        onCancel={() => setFieldPromptOpen(false)}
      />
    </div>
  );
}
